import React, { useEffect, useRef, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import FinalPayable from './final_payable';
import SessionDrilldownModal from './session_drilldown_modal';
import ShadowBanner from './shadow_banner';
import WhyPopover from './why_popover';
import SessionDateLink from '../shared/session_date_link';
import { fetch_l2, post_recalc } from '../api';
import { api_error, fmt, is_permission_error, load_failed_text } from '../format';
import { NEEDS_REVIEW_TEXT, l2_view, stale_text } from './adapters';
import { RECALC_DONE, recalc_failure, recalc_outcome } from '../reports/adapters';
import { recalc_payload } from '../additional_payments/upload/requests';
import { DRILLDOWN_KIND } from './params';
import { correction_cells, deduction_cell, not_charged_view, plain_cell } from './l1_ledger';
import InfoLedger from './info_ledger';
import { with_ist } from '../shared/ist';
import { Ledger } from '../shared/cells';
import { audit_link } from '../audit/params';
import { rate_changes_text } from '../base_rate/logic';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import {
  additional_entry_csv_columns,
  final_payable_csv_columns,
  history_csv_columns,
  l2_incentive_csv_columns,
  l2_summary_csv_columns,
  overview_csv_name
} from './csv_columns';

/*
 * Overview Level 2: one teacher's breakdown for the pay month. Read-only: penalty edits live on
 * the Penalties tab.
 */
// Sorts of the values inside the money ledger (no header of their own: the sort select offers them).
const L2_LEDGER_SORTS = [
  { sort_key: 'amount', label: 'Amount', sort_type: 'number' },
  { sort_key: 'penalty_amount', label: 'Penalty', sort_type: 'number' },
  { sort_key: 'waived_amount', label: 'Waivers', sort_type: 'number' },
  { sort_key: 'correction_amount', label: 'Corrections', sort_type: 'number' },
  { sort_key: 'free_pass_amount', label: 'Free Pass', sort_type: 'number' },
  { sort_key: 'uncharged_orphan_amount', label: 'Uncharged orphans', sort_type: 'number' }
];

const SUB_TABS = [
  { key: 'summary', label: 'Payment summary' },
  { key: 'incentives', label: 'Incentives & additions' },
  { key: 'final', label: 'Final payable' },
  { key: 'history', label: 'Computation history' }
];

const OverviewLevel2 = ({
  pay_month,
  month_label,
  teacher,
  on_back,
  on_error,
  on_open_penalties
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [sub_tab, setSubTab] = useState('summary');
  const [drill, setDrill] = useState(null);
  const [summary_sort, setSummarySort] = useState(null);
  // bumped on every fetch (incl. retry) so a stale response can never land after a newer one.
  const [reload_token, setReloadToken] = useState(0);
  // the in-place "Recalculate now": idle | running, plus the outcome when it did not recalculate.
  const [recalc, setRecalc] = useState({ running: false, text: '' });
  // the teacher-month on screen, so a recalc answer for another one is dropped.
  const shown_key = useRef('');

  const teacher_uuid = teacher && teacher.teacher_uuid;
  shown_key.current = `${teacher_uuid}|${pay_month}`;

  useEffect(() => {
    setRecalc({ running: false, text: '' });
  }, [teacher_uuid, pay_month]);

  useEffect(() => {
    if (!teacher_uuid || !pay_month) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetch_l2({ teacher_uuid, pay_month })
      .then((res) => {
        if (cancelled) return;
        setData(l2_view(res));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        // Permission errors also toast (module-wide gate); other failures are inline-only, matching Penalties.
        if (is_permission_error(e)) on_error(e, 'Failed to load the teacher payment breakdown.');
        setData(null);
        setLoading(false);
        // shows the server's own LOOKUP_FAILED sentence when the body carries one.
        setLoadError(load_failed_text(e, 'Failed to load the teacher payment breakdown.'));
      });
    return () => {
      cancelled = true;
    };
  }, [teacher_uuid, pay_month, on_error, reload_token]);

  const back_button = (
    <button type="button" className="tp-btn-out tp-l2-back" onClick={on_back}>
      ← Back to all teachers
    </button>
  );

  if (loading) {
    return (
      <div>
        {back_button}
        <CustomLoading />
      </div>
    );
  }
  if (load_error) {
    return (
      <div>
        {back_button}
        <LoadFailed text={load_error} on_retry={() => setReloadToken((n) => n + 1)} />
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        {back_button}
        <div className="tp-empty">No breakdown available for this teacher and month.</div>
      </div>
    );
  }

  const { meta, summary, incentives, additional, history } = data;
  const rate_steps = rate_changes_text(meta.base_rate_snapshot, meta.rate_changes_in_month);
  const stale = data.needs_review;

  // Same per-teacher recalc as Reports; a done month reloads, else the outcome says why.
  const run_recalc = () => {
    if (recalc.running) return;
    const key = shown_key.current;
    setRecalc({ running: true, text: '' });
    post_recalc(recalc_payload(teacher_uuid, pay_month))
      .then(recalc_outcome)
      .catch((e) => {
        if (is_permission_error(e)) {
          on_error(e, 'You do not have access to recalculate teacher payments.');
        }
        return recalc_failure(api_error(e).message);
      })
      .then((outcome) => {
        if (shown_key.current !== key) return;
        setRecalc({ running: false, text: outcome.kind === RECALC_DONE ? '' : outcome.text });
        if (outcome.kind === RECALC_DONE) setReloadToken((n) => n + 1);
      });
  };

  const open_drill = (row, kind) =>
    setDrill({
      category: row.category,
      category_label: kind === DRILLDOWN_KIND.PENALTIES ? `${row.label} — penalties` : row.label,
      kind
    });

  const penalty_drill_target = (r) =>
    r.is_unattributed ? { category: r.category, label: 'Not linked to a session type' } : r;

  // The category's money as a ledger: earned amount, then the charged penalty; Net is the decision.
  const ledger_items = (r) => {
    const penalty = deduction_cell(r.penalty_amount, true);
    const { raised } = correction_cells(r.correction_amount, true);
    return [
      r.is_unattributed ? null : { label: 'Amount', ...plain_cell(r.amount, true) },
      {
        label: 'Penalty',
        tone: penalty.tone,
        value: r.can_drill_penalties ? (
          <button
            type="button"
            className="tp-why-trigger tp-ov-pen"
            onClick={() => open_drill(penalty_drill_target(r), DRILLDOWN_KIND.PENALTIES)}
          >
            {penalty.value}
          </button>
        ) : (
          penalty.value
        )
      },
      raised ? { label: 'Raised by Ops', ...raised } : null
    ];
  };
  // Never charged, so not in Net: shown apart, unsigned.
  const info_view = (r) =>
    not_charged_view(
      [
        { label: 'Waived', amount: r.waived_amount },
        correction_cells(r.correction_amount).not_charged
          ? { label: 'Corrections', amount: r.correction_amount }
          : null,
        { label: 'Free Pass', amount: r.free_pass_amount },
        { label: 'Uncharged orphans', amount: r.uncharged_orphan_amount }
      ],
      true
    );

  // Four tracks: category · sessions · money ledger · net. Nothing folds; cards below 660px.
  const summary_columns = [
    {
      key: 'category',
      role: 'identifier',
      label: 'Category',
      sort_key: 'category',
      sort_type: 'text',
      sort_value: (r) => r.label,
      render: (r) =>
        r.is_unattributed ? (
          <span
            className="tp-c-name"
            title="Manual or month-level penalties, and rows computed before penalties carried a session type"
          >
            Penalties not linked to a session type
          </span>
        ) : (
          <span className="tp-c-name">{r.label}</span>
        )
    },
    {
      key: 'sessions_count',
      label: 'Sessions',
      align: 'right',
      track: { compact: '88px', full: '100px' },
      sort_key: 'sessions_count',
      sort_type: 'number',
      render: (r) => {
        if (r.is_unattributed) return <span className="tp-muted">—</span>;
        return r.can_drill_items ? (
          <button
            type="button"
            className="tp-link"
            onClick={() => open_drill(r, DRILLDOWN_KIND.ALL)}
          >
            {r.sessions_count}
          </button>
        ) : (
          r.sessions_count
        );
      },
      card_render: (r) => {
        if (r.is_unattributed) return null;
        const text = `${r.sessions_count} ${r.sessions_count === 1 ? 'session' : 'sessions'}`;
        return r.can_drill_items ? (
          <button
            type="button"
            className="tp-link"
            onClick={() => open_drill(r, DRILLDOWN_KIND.ALL)}
          >
            {text}
          </button>
        ) : (
          <span className="tp-c-line">{text}</span>
        );
      }
    },
    {
      key: 'breakdown',
      label: 'Money',
      align: 'right',
      track: { compact: '220px', full: '260px' },
      sorts: L2_LEDGER_SORTS.map(({ sort_key, label }) => ({ sort_key, label })),
      render: (r) => (
        <>
          <Ledger items={ledger_items(r)} />
          <InfoLedger view={info_view(r)} />
        </>
      )
    },
    {
      // Status track: in a card the net sits top right, beside the category.
      key: 'net_amount',
      role: 'status',
      label: 'Net',
      align: 'right',
      track: { compact: '120px', full: '140px' },
      sort_key: 'net_amount',
      sort_type: 'number',
      render: (r) =>
        r.is_unattributed ? (
          <span className="tp-muted">—</span>
        ) : (
          <span className="tp-c-amt">{fmt(r.net_amount, 2)}</span>
        )
    }
  ];

  // The unattributed line always stays last, in either sort direction: it is a catch-all bucket,
  // not a category that competes on the sorted column.
  const sorted_summary_rows = sort_rows(
    summary.rows,
    [...summary_columns, ...L2_LEDGER_SORTS],
    summary_sort
  );
  const summary_rows = summary.unattributed
    ? [...sorted_summary_rows, { ...summary.unattributed, is_unattributed: true }]
    : sorted_summary_rows;
  const on_summary_sort = (key) => setSummarySort((s) => toggle_sort(s, key));

  // Each card's CSV: the rows on screen, in their on-screen order.
  const csv_button = (table, columns, rows, label) => (
    <span className="tp-card-hd-acts">
      <DownloadCsvButton
        columns={columns}
        rows={rows}
        file_name={() => overview_csv_name(table, pay_month)}
        label={label}
      />
    </span>
  );

  const incentive_columns = [
    { key: 'category_label', role: 'identifier', label: 'Category', min_width: 140 },
    {
      key: 'item_type',
      label: 'Type',
      render: (i) => <span className="tp-sub">{i.item_type}</span>
    },
    {
      key: 'amount',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      render: (i) => (
        <WhyPopover
          detail={i.explain}
          amount_node={<span className="tp-pos">{i.amount_text}</span>}
        />
      )
    },
    {
      key: 'session',
      role: 'actions',
      label: 'Related session',
      // the session's name (student, class or workshop) opens its own details page
      render: (i) =>
        i.session_uuid ? (
          <SessionDateLink uuid={i.session_uuid} kind={i.session_kind}>
            {i.session_link_text}
          </SessionDateLink>
        ) : (
          '—'
        ),
      card_render: (i) =>
        i.session_uuid ? (
          <SessionDateLink uuid={i.session_uuid} kind={i.session_kind}>
            {i.session_link_text}
          </SessionDateLink>
        ) : null
    }
  ];

  // Ref, Source and Added by fold under their neighbours on narrower containers (still shown).
  const additional_columns = [
    { key: 'type_label', role: 'identifier', label: 'Type', min_width: 100 },
    {
      key: 'date_text',
      role: 'date',
      label: 'Date / time (IST)',
      align: 'right',
      render: (a) => <span style={{ fontSize: 11 }}>{a.date_text}</span>,
      // A card's line 2 drops a missing date instead of printing a lone dash.
      // Cards have no header, so the time carries its own IST.
      card_render: (a) => (a.date_text && a.date_text !== '—' ? with_ist(a.date_text) : null)
    },
    { key: 'category_label', label: 'Category', min_width: 140 },
    {
      key: 'reference_id',
      label: 'Ref',
      fold: { into: 'category_label', order: 1 },
      render: (a) => <span className="tp-mono">{a.reference_id || '—'}</span>
    },
    {
      key: 'source_text',
      label: 'Source',
      fold: { into: 'type_label', order: 3 }
    },
    {
      key: 'amount',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      render: (a) => <span className={a.amount_tone}>{a.amount_text}</span>
    },
    {
      key: 'why',
      label: 'Why',
      render: (a) =>
        a.explain && a.explain.rule_ref ? (
          <WhyPopover
            detail={a.explain}
            label="Why this entry?"
            amount_node={<span>{a.explain.rule_ref}</span>}
          />
        ) : (
          '—'
        )
    },
    {
      key: 'note',
      label: 'Note',
      min_width: 160,
      render: (a) => (
        <span className="tp-sub" title={a.note}>
          {a.note || '—'}
        </span>
      )
    },
    {
      key: 'added_by',
      label: 'Added by',
      fold: { into: 'note', order: 2 },
      render: (a) => (
        <span style={{ fontSize: 11 }} title={a.added_by_title || undefined}>
          {a.added_by}
        </span>
      )
    }
  ];

  const history_columns = [
    {
      key: 'when',
      role: 'date',
      label: 'When',
      align: 'right',
      render: (h) => <span style={{ fontSize: 11 }}>{h.when}</span>,
      card_render: (h) => (h.when && h.when !== '—' ? h.when : null)
    },
    { key: 'action', role: 'identifier', label: 'Trigger', min_width: 120 },
    { key: 'actor', label: 'Actor', min_width: 100 },
    {
      key: 'reason',
      label: 'Reason',
      min_width: 200,
      render: (h) => <span style={{ fontSize: 11 }}>{h.reason}</span>
    }
  ].concat(
    history.show_net
      ? [
          {
            key: 'net_text',
            role: 'amount',
            label: 'Net',
            align: 'right',
            render: (h) => h.net_text || '—'
          }
        ]
      : []
  );

  return (
    <div className="tp-l2">
      <div className="tp-l2-bar">
        {back_button}
        <a className="tp-link" href={audit_link(pay_month, teacher_uuid)}>
          All activity this month
        </a>
      </div>

      <ShadowBanner show={data.shadow_banner} />

      <div className="tp-meta-grid">
        <div className="tp-meta-field">
          <div className="tp-meta-k">Teacher</div>
          <div className="tp-meta-v tp-meta-name">
            {meta.teacher_name || (teacher && teacher.teacher_name) || '--'}
          </div>
          <TeacherSlugLine slug={meta.teacher_slug || (teacher && teacher.teacher_slug)} />
        </div>
        <div className="tp-meta-field">
          {/* a month with a dated rate change shows each step; a flat month the snapshot alone. */}
          <div className="tp-meta-k">{rate_steps ? 'Base rate' : 'Base rate (month snapshot)'}</div>
          <div className="tp-meta-v">{rate_steps || fmt(meta.base_rate_snapshot)}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Free Pass balance</div>
          <div className="tp-meta-v">
            {meta.free_pass_balance === null || meta.free_pass_balance === undefined
              ? '--'
              : meta.free_pass_balance}
          </div>
        </div>
        {/* GST ₹0 already means not GST-registered, so no separate status field. */}
        {[
          ['Gross', data.gross],
          ['GST', data.gst],
          ['TDS', data.tds]
        ].map(([k, v]) => (
          <div className="tp-meta-field" key={k}>
            <div className="tp-meta-k">{k}</div>
            <div className="tp-meta-v tp-nowrap">
              {stale ? (
                <span className="tp-neg">{NEEDS_REVIEW_TEXT}</span>
              ) : v === null || v === undefined ? (
                '--'
              ) : (
                fmt(v, 2)
              )}
            </div>
          </div>
        ))}
        <div className="tp-meta-field tp-meta-lead">
          <div className="tp-meta-k">Payable</div>
          <div className="tp-meta-v tp-nowrap">
            {stale ? (
              <span className="tp-neg">{NEEDS_REVIEW_TEXT}</span>
            ) : data.not_priced_text ? (
              <span className="tp-hint">{data.not_priced_text}</span>
            ) : (
              fmt(data.payable_amount, 2)
            )}
          </div>
        </div>
      </div>

      {stale ? (
        <div className="tp-banner tp-banner-error">
          <span>⚠ {stale_text(meta)}</span>{' '}
          <button
            type="button"
            className="tp-btn-out"
            aria-disabled={recalc.running ? 'true' : undefined}
            onClick={run_recalc}
          >
            {recalc.running ? 'Recalculating…' : 'Recalculate now'}
          </button>
          {recalc.running || recalc.text ? (
            <div role="status" className="tp-hint">
              {recalc.running ? 'Recalculating this month…' : recalc.text}
            </div>
          ) : null}
        </div>
      ) : null}
      {!stale && data.not_priced_text ? (
        <div className="tp-banner tp-banner-info">
          {data.not_priced_text}. Not paid through this system: the month is not calculated and
          closes as a zero month.
        </div>
      ) : null}

      <div className="tp-seg tp-seg-wrap tp-l2-tabs" role="group" aria-label="Breakdown view">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={sub_tab === t.key ? 'on' : ''}
            aria-pressed={sub_tab === t.key}
            onClick={() => setSubTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub_tab === 'summary' ? (
        <div className="tp-card tp-l2-summary">
          <div className="tp-card-hd">
            <span className="tp-card-hd-main">
              <span className="tp-card-hd-title">Payment summary</span>
              <span className="tp-card-hd-sub">
                Click a session count or penalty for session-level detail
              </span>
            </span>
            {csv_button('payment-summary', l2_summary_csv_columns(teacher_uuid), summary_rows)}
          </div>
          <DataTable
            columns={summary_columns}
            rows={summary_rows}
            rowKey={(r) => r.category}
            sort={summary_sort}
            on_sort={on_summary_sort}
            on_sort_set={setSummarySort}
            emptyText="No payment items for this month."
            card_below={660}
            responsive
          />
        </div>
      ) : null}

      {sub_tab === 'incentives' ? (
        <>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">System-calculated incentives</span>
              {csv_button('incentives', l2_incentive_csv_columns(teacher_uuid), incentives)}
            </div>
            <DataTable
              columns={incentive_columns}
              rows={incentives}
              rowKey={(i) => i.key}
              emptyText="No incentives for this month."
              responsive
            />
          </div>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Additional payments</span>
              <span className="tp-card-hd-sub">
                Inside gross, before GST and TDS (PRD 22.4) · entered on the Additional payments tab
              </span>
              {csv_button(
                'additional-payments',
                additional_entry_csv_columns(teacher_uuid),
                additional
              )}
            </div>
            <DataTable
              columns={additional_columns}
              rows={additional}
              rowKey={(x) => x.key}
              emptyText="No additional payments for this month."
              responsive
            />
          </div>
        </>
      ) : null}

      {sub_tab === 'final' ? (
        <FinalPayable
          lines={data.final_payable}
          gst_registered={!!meta.is_gst_registered}
          csv_action={csv_button(
            'final-payable',
            final_payable_csv_columns(teacher_uuid),
            data.final_payable
          )}
        />
      ) : null}

      {sub_tab === 'history' ? (
        <div className="tp-card">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title">Computation history</span>
            <span className="tp-card-hd-sub">
              Recalc audit rows — every change to this month is explainable
            </span>
            {csv_button('computation-history', history_csv_columns(teacher_uuid), history.rows)}
          </div>
          <DataTable
            columns={history_columns}
            rows={history.rows}
            rowKey={(h) => h.key}
            emptyText="No recomputations recorded for this month."
            responsive
          />
        </div>
      ) : null}

      <SessionDrilldownModal
        show={!!drill}
        close={() => setDrill(null)}
        pay_month={pay_month}
        month_label={month_label}
        teacher_uuid={teacher_uuid}
        category={drill ? drill.category : ''}
        category_label={drill ? drill.category_label : ''}
        kind={drill ? drill.kind : ''}
        on_error={on_error}
      />

      <div className="tp-card-hd-sub tp-l2-readonly" style={{ marginTop: 10, display: 'block' }}>
        This page is read-only. Edit penalties on the{' '}
        {on_open_penalties ? (
          <button type="button" className="tp-link" onClick={on_open_penalties}>
            Penalties
          </button>
        ) : (
          'Penalties'
        )}{' '}
        tab.
      </div>
    </div>
  );
};

export default OverviewLevel2;
